import {BASE_API_URL, WEBDB_S3_URL} from '../envs.ts';
import type {FeatureLike} from 'ol/Feature';
import {getLanguageCode} from '../i18n.ts';
import type {SeasonCode} from '../layers/layerTypes';
import {isSeasonCode} from '../layers/layerHelpers.ts';
import {isDateBetween} from '../utils.ts';
import {maintenanceStatus$} from '../stores/store-main.ts';

export class Addressable {
  addressName?: string;
  addressName2?: string;
  addressStreet?: string;
  addressZip?: string;
  addressCity?: string;
  addressPhone?: string;
  addressEmail?: string;
  addressUrl?: string;
  addressUrlText?: string;
  addressMobile?: string;
  urlBookingLink?: string;
  urlBookingText?: string;

  constructor(data: FeatureLike | Addressable) {
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      this.addressName = data.get('name');
      this.addressName2 = data.get('name_2');
      this.addressStreet = data.get('street');
      this.addressZip = data.get('zip');
      this.addressCity = data.get('place');
      this.addressPhone = data.get('tel');
      this.addressMobile = data.get('mobile');
      this.addressEmail = data.get('email');
      this.addressUrl = data.get('url1_link');
      this.addressUrlText = data.get('url1_text');
      this.urlBookingLink = data.get('url2_link');
      this.urlBookingText = data.get('url2_text');
    } else {
      Object.assign(this, <Addressable>data);
    }
  }
}

export type Gallery = {
  src?: string;
  srcBig?: string;
  author?: string;
  copyright?: string;
  abstract: string;
  urls?: Record<string, string>;
  urlMaster?: string;
};

export class PlacesBase extends Addressable {
  id: number;
  refid: number;
  title: string;
  abstract: string;
  photo: string;
  photoBig: string;
  photoSmall: string;
  photoAbstract: string;
  description: string;
  gallery: Gallery[];

  constructor(data: PlacesBase | FeatureLike) {
    super(data);
    if (typeof (<FeatureLike>data).get === 'function') {
      data = <FeatureLike>data;
      const bigGallery = data.get('photo_gallery_big');
      this.id = data.get('id');
      this.title = data.get('title');
      this.abstract = data.get('abstract');
      this.photo = data.get('foto');
      this.description = data.get('description');
      this.gallery = data
        .get('photo_gallery')
        .map((src: string, indx: number) => {
          return {
            src: src,
            srcBig: bigGallery[indx] || src,
          };
        });
    } else {
      Object.assign(this, <PlacesBase>data);
    }
  }
}

export type AdminMessageType = 'error' | 'warning' | 'success' | 'information';
export type AdminMessage = {
  text: string;
  title?: string;
  urlText?: string;
  urlLink?: string;
  type?: AdminMessageType;
};

export async function fetchAdminMessage(): Promise<AdminMessage | null> {
  const propagatedParams =
    typeof document !== 'undefined' ? document.location.search : '';
  const response = await fetch(
    `${WEBDB_S3_URL}/banners/${getLanguageCode()}/banners.json${propagatedParams}`,
  )
    .then((r) => (r.status >= 300 ? Promise.reject('Error') : r.json()))
    .catch((r): any[] => {
      return [];
    });
  if (!response.length) return null;
  switch (response[0].titleBackgroundColor?.color) {
    case '#8CE99A':
      response[0].type = 'success';
      break;
    case '#FFC078':
      response[0].type = 'warning';
      break;
    case '#FFA8A8':
      response[0].type = 'error';
      break;
    default:
      response[0].type = undefined;
  }
  return response[0];
}

export async function updateMaintenanceStatus(): Promise<void> {
  try {
    const response = await fetch(`${BASE_API_URL}/api/4/loginConfig`);
    if (response.status === 200) {
      maintenanceStatus$.next(await response.json());
    }
  } catch {
    console.error('Could not update mstatus');
  }
}

export type Feedback = {
  captcha: string;
  message: string;
  firstName?: string;
  secondName?: string;
  email?: string;
  file?: File;
  phoneNumber?: string;
};

export async function postFeedback(feedback: Feedback): Promise<Response> {
  const formData = new FormData();
  Object.entries(feedback).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(
        key,
        value instanceof Boolean ? value.toString() : (value as string | File),
      );
    }
  });

  return fetch(`${BASE_API_URL}/api/5/feedback`, {
    method: 'POST',
    body: formData,
  });
}

export async function fetchSeason(): Promise<SeasonCode> {
  try {
    const propagatedParams =
      typeof document !== 'undefined' ? document.location.search : '';
    const response = await fetch(
      `${WEBDB_S3_URL}/season_dates/en/season_dates.json${propagatedParams}`,
    );
    const json: {title: string; startDate: string}[] = await response.json();
    const dates: {code: SeasonCode; startDate: Date}[] = json
      .filter((s) => isSeasonCode(s.title.toLowerCase()))
      .map((s) => {
        return {
          code: <SeasonCode>s.title.toLowerCase(),
          startDate: new Date(s.startDate),
        };
      });
    if (isDateBetween(dates[0].startDate, dates[1].startDate, new Date())) {
      return dates[0].code;
    } else {
      return dates[1].code;
    }
  } catch (e) {
    console.error(e);
    return getDefaultSeasonFallback();
  }
}

/**
 * The default season depends on the time of the year.
 */
function getDefaultSeasonFallback(): SeasonCode {
  /**
   * Summer start and end dates used to determine the season.
   * The year is not important so it's set to 1970.
   */
  const summerStart = new Date('1970-03-15');
  const summerEnd = new Date('1970-11-21');
  if (isDateBetween(summerStart, summerEnd, new Date())) {
    return 'summer';
  } else {
    return 'winter';
  }
}
